import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Chip,
  Divider,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

/**
 * "idle" is powered but not producing — the middle band of the extruder
 * thresholds on the Settings page. It draws green like "running", because an
 * idle blender is healthy and operators read amber as a problem; the chip
 * still says "Idle" so the distinction is not lost, just de-alarmed.
 */
export type ComponentState =
  | "off"
  | "running"
  | "idle"
  | "unknown"
  | "warning"
  | "error";

export type DiagramComponent = {
  /** Filename stem; image URL becomes `${basePath}/${key}_${variant}.png` */
  key: string;
  /** Human-readable label shown in the toggle chip */
  label: string;
};

/**
 * One row of a component's "how this is colored" legend: a state, and the
 * plant condition that produces it, in the words an operator would use.
 *
 * These live with the rules in `statusMapping.ts` rather than here, so the
 * prose sits next to the code it describes.
 */
export type StateRule = { state: ComponentState; when: string };

/** One value a rule was evaluated against, formatted for display. */
export type Reading = { label: string; value: string };

/**
 * What a component's hover card shows beyond its color: the rules that decide
 * that color, the readings they were applied to, and how old those readings
 * are — so "why is this red?" is answerable without leaving the diagram.
 *
 * Every field is optional; a component with no detail still gets a card naming
 * it and its state.
 */
export type ComponentDetail = {
  /** Legend rows, in severity order. The one that fired is highlighted. */
  rules?: StateRule[];
  /** The reading behind the state, most relevant first. */
  readings?: Reading[];
  /** How current those readings are, e.g. "Read 12s ago". */
  freshness?: string;
  /** Shown above the rules — for machines with no signal to judge at all. */
  note?: string;
};

type Props = {
  /** Public URL prefix where overlay PNGs live, e.g. "/diagrams/lam" */
  basePath: string;
  /** Components to layer, in z-order (later items render on top) */
  components: DiagramComponent[];
  /** Current state per component key */
  state: Record<string, ComponentState>;
  /** Hover-card content per component key */
  details?: Record<string, ComponentDetail>;
  /** Called when the user clicks a component's toggle chip */
  onToggle?: (key: string) => void;
  /** Show/hide the per-component toggle controls (default: true) */
  showControls?: boolean;
  /**
   * Fired once every state variant has been fetched (or failed). Until then the
   * diagram can only paint art the browser already has, so a parent showing a
   * loading overlay should keep it up.
   */
  onImagesReady?: () => void;
};

export const STATE_TO_VARIANT: Record<ComponentState, string> = {
  off: "black",
  running: "green",
  idle: "green",
  unknown: "black",
  warning: "orange",
  error: "red",
};

export const STATE_COLORS: Record<ComponentState, string> = {
  off: "#616161",
  running: "#2e7d32",
  idle: "#2e7d32",
  unknown: "#9e9e9e",
  warning: "#ed6c02",
  error: "#d32f2f",
};

export const STATE_LABELS: Record<ComponentState, string> = {
  off: "Off",
  running: "Running",
  idle: "Idle",
  // Distinct from "Off": we are not receiving readings, so the real state is
  // unknown rather than known-stopped.
  unknown: "No data",
  warning: "Warning",
  error: "Error",
};

/** Native aspect ratio of every overlay PNG (1085 × 408). */
const ASPECT = `${1085} / ${408}`;

// ---- Hover targets ----
/**
 * The overlays are full-frame PNGs, so there is no element per machine to hang
 * a tooltip on — every layer covers the whole diagram and the ones on top would
 * swallow every pointer event. Instead each component's artwork is traced once
 * into a 1-byte-per-pixel ink mask and the pointer is tested against the masks.
 *
 * The variants differ only in color, not shape, so one mask per component is
 * enough; it is built from the black art, which is also the fallback state and
 * so is certain to exist. The PNGs are same-origin, so the canvas is not
 * tainted and `getImageData` is allowed. If any of that fails the masks are
 * simply absent: the artwork stops being hoverable and the chips below, which
 * carry the same card, are unaffected.
 */
const HIT_VARIANT = "black";

/**
 * How far off a machine the pointer may be and still count as over it, in
 * source-image pixels (~4 on screen at the size the card renders). Enough that
 * thin linework is not a pixel hunt, small enough not to reach a neighbour.
 */
const HIT_RADIUS = 10;

/** Anything not near-white and not transparent is that layer's artwork. */
const INK_MAX_LUMA = 235;

type Mask = { w: number; h: number; ink: Uint8Array };

/**
 * A zero-size rect at a point, as the popper's virtual anchor.
 *
 * Built by hand rather than with `new DOMRect`, which jsdom does not provide
 * and older browsers only partly do — the anchor is read for its edges, so a
 * plain literal is all Popper ever wants from it.
 */
const pointRect = (x: number, y: number): DOMRect =>
  ({
    x,
    y,
    left: x,
    right: x,
    top: y,
    bottom: y,
    width: 0,
    height: 0,
    toJSON: () => ({ x, y }),
  } as DOMRect);

const buildMask = (img: HTMLImageElement): Mask | null => {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) return null;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0);
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch {
    // Tainted canvas (art served cross-origin). No masks, no artwork hover.
    return null;
  }
  const ink = new Uint8Array(w * h);
  for (let p = 0, i = 0; p < ink.length; p++, i += 4) {
    if (data[i + 3] < 16) continue;
    const luma = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
    if (luma <= INK_MAX_LUMA) ink[p] = 1;
  }
  return { w, h, ink };
};

/**
 * Squared distance from (x, y) to the nearest ink pixel within `r`, or null if
 * there is none. Squared is enough: it is only ever compared against another.
 */
const nearestInk = (
  m: Mask,
  x: number,
  y: number,
  r: number
): number | null => {
  let best: number | null = null;
  const x0 = Math.max(0, x - r);
  const x1 = Math.min(m.w - 1, x + r);
  const y0 = Math.max(0, y - r);
  const y1 = Math.min(m.h - 1, y + r);
  for (let yy = y0; yy <= y1; yy++) {
    const row = yy * m.w;
    const dy = yy - y;
    for (let xx = x0; xx <= x1; xx++) {
      if (!m.ink[row + xx]) continue;
      const dx = xx - x;
      const d = dx * dx + dy * dy;
      if (best == null || d < best) best = d;
    }
  }
  return best;
};

// ---- Hover card ----
const CARD_BG = "rgba(38,38,38,0.96)";
const CARD_SX = {
  bgcolor: CARD_BG,
  color: "#fff",
  maxWidth: 360,
  p: 1.25,
} as const;

const Swatch: React.FC<{ state: ComponentState }> = ({ state }) => (
  <Box
    component="span"
    sx={{
      width: 9,
      height: 9,
      mt: "4px",
      flexShrink: 0,
      borderRadius: "50%",
      display: "inline-block",
      bgcolor: STATE_COLORS[state],
      // Grey and dark green both disappear into the card without this.
      outline: "1px solid rgba(255,255,255,0.35)",
    }}
  />
);

/**
 * What a component's color means and what produced it.
 *
 * Exported so the artwork's popper and the chips below it render exactly the
 * same thing — hovering the machine and hovering its chip should not be two
 * different answers.
 */
export const ComponentStateCard: React.FC<{
  label: string;
  state: ComponentState;
  detail?: ComponentDetail;
  /** Extra line at the foot, e.g. the manual-toggle hint. */
  hint?: string;
}> = ({ label, state, detail, hint }) => {
  const rules = detail?.rules ?? [];
  // Highlight the rule that fired. Exact state first, then whichever rule
  // paints the same color: "idle" and "running" share green, so an idle line
  // should light up its green row rather than none at all.
  let active = rules.findIndex((r) => r.state === state);
  if (active < 0)
    active = rules.findIndex(
      (r) => STATE_TO_VARIANT[r.state] === STATE_TO_VARIANT[state]
    );

  const heading = (text: string) => (
    <Typography
      variant="caption"
      sx={{ display: "block", opacity: 0.65, letterSpacing: 0.4, mb: 0.25 }}
    >
      {text}
    </Typography>
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          {label}
        </Typography>
        <Box sx={{ mt: "-4px" }}>
          <Swatch state={state} />
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {STATE_LABELS[state]}
        </Typography>
      </Stack>

      {detail?.note && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
          {detail.note}
        </Typography>
      )}

      {rules.length > 0 && (
        <>
          <Divider sx={{ my: 0.85, borderColor: "rgba(255,255,255,0.18)" }} />
          {heading("How this is colored")}
          <Stack spacing={0.4}>
            {rules.map((r, i) => (
              <Stack
                key={`${r.state}-${r.when}`}
                direction="row"
                spacing={0.75}
                alignItems="flex-start"
                // The row that fired is the answer; the rest are context.
                sx={{ opacity: i === active ? 1 : 0.5 }}
              >
                <Swatch state={r.state} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: i === active ? 700 : 400 }}
                >
                  {r.when}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}

      {!!detail?.readings?.length && (
        <>
          <Divider sx={{ my: 0.85, borderColor: "rgba(255,255,255,0.18)" }} />
          {heading("Latest reading")}
          <Stack spacing={0.25}>
            {detail.readings.map((r) => (
              <Stack
                key={r.label}
                direction="row"
                spacing={1.5}
                justifyContent="space-between"
              >
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  {r.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, textAlign: "right" }}
                >
                  {r.value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}

      {(detail?.freshness || hint) && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.85, opacity: 0.65 }}
        >
          {[detail?.freshness, hint].filter(Boolean).join(" · ")}
        </Typography>
      )}
    </Box>
  );
};

const ProductionLineDiagram: React.FC<Props> = ({
  basePath,
  components,
  state,
  details,
  onToggle,
  showControls = true,
  onImagesReady,
}) => {
  const [masks, setMasks] = useState<Record<string, Mask>>({});
  /** Which machine the pointer is over, and where it entered, in image px. */
  const [hover, setHover] = useState<{ key: string; x: number; y: number } | null>(
    null
  );
  const frameRef = useRef<HTMLDivElement | null>(null);

  // Warm every state variant up front (~4KB each). Without this the browser
  // only fetches e.g. the green PNG at the moment status arrives, so the
  // diagram lingers on the black "off" art after loading has finished. The
  // black pass doubles as the source of the hover masks.
  useEffect(() => {
    let cancelled = false;
    const variants = Array.from(new Set(Object.values(STATE_TO_VARIANT)));
    const jobs = components.flatMap((c) =>
      variants.map((v) => ({ key: c.key, variant: v }))
    );

    const built: Record<string, Mask> = {};
    let remaining = jobs.length;
    const done = () => {
      // Report ready even on error, so a missing PNG can't wedge the overlay.
      if (--remaining > 0 || cancelled) return;
      setMasks(built);
      onImagesReady?.();
    };

    if (!jobs.length) {
      onImagesReady?.();
      return;
    }

    for (const job of jobs) {
      const img = new Image();
      img.onload = () => {
        if (job.variant === HIT_VARIANT) {
          const m = buildMask(img);
          if (m) built[job.key] = m;
        }
        done();
      };
      img.onerror = done;
      img.src = `${basePath}/${job.key}_${job.variant}.png`;
    }

    return () => {
      cancelled = true;
    };
  }, [basePath, components, onImagesReady]);

  /**
   * Where the artwork actually sits inside the frame. `object-fit: contain`
   * letterboxes it, so the frame's own box is not the image's box.
   *
   * Takes the frame's rect rather than reading it, so a pass over every
   * component costs one forced layout instead of one per machine.
   */
  const boxIn = useCallback(
    (r: DOMRect, m: Mask) => {
      const scale = Math.min(r.width / m.w, r.height / m.h);
      return {
        scale,
        left: r.left + (r.width - m.w * scale) / 2,
        top: r.top + (r.height - m.h * scale) / 2,
      };
    },
    []
  );

  const layout = useCallback(
    (m: Mask) => {
      const el = frameRef.current;
      return el ? boxIn(el.getBoundingClientRect(), m) : null;
    },
    [boxIn]
  );

  const handleMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = frameRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();

      // Topmost first, but the nearest machine wins outright — the layers are
      // disjoint, so z-order only ever breaks a tie.
      let best: { key: string; d: number } | null = null;
      let point = { x: 0, y: 0 };
      for (let i = components.length - 1; i >= 0; i--) {
        const key = components[i].key;
        const m = masks[key];
        if (!m) continue;
        const box = boxIn(rect, m);
        const x = Math.round((e.clientX - box.left) / box.scale);
        const y = Math.round((e.clientY - box.top) / box.scale);
        const d = nearestInk(m, x, y, HIT_RADIUS);
        if (d != null && (best == null || d < best.d)) {
          best = { key, d };
          point = { x, y };
        }
      }
      // Re-anchoring on every pixel of travel would re-render the card
      // continuously; like a normal tooltip it stays where it opened.
      if (best?.key === hover?.key) return;
      setHover(best ? { key: best.key, ...point } : null);
    },
    [components, masks, boxIn, hover]
  );

  const anchorEl = useMemo(() => {
    if (!hover) return null;
    const m = masks[hover.key];
    if (!m) return null;
    return {
      // Resolved on each poll of the popper rather than captured, so the card
      // keeps its place if the page scrolls or the diagram is resized.
      getBoundingClientRect: () => {
        const box = layout(m);
        if (!box) return pointRect(0, 0);
        return pointRect(
          box.left + hover.x * box.scale,
          box.top + hover.y * box.scale
        );
      },
    };
  }, [hover, masks, layout]);

  const hoveredComponent = components.find((c) => c.key === hover?.key);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        ref={frameRef}
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: ASPECT,
          // Fallback for browsers without aspect-ratio support:
          "@supports not (aspect-ratio: 1 / 1)": {
            pb: `${(408 / 1085) * 100}%`,
            height: 0,
          },
          backgroundColor: "background.paper",
          borderRadius: 1,
          overflow: "hidden",
          cursor: hover ? "help" : "default",
        }}
      >
        {components.map((c) => {
          const s = state[c.key] ?? "off";
          const variant = STATE_TO_VARIANT[s];
          const src = `${basePath}/${c.key}_${variant}.png`;
          return (
            <img
              key={c.key}
              src={src}
              alt={c.label}
              draggable={false}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                pointerEvents: "none",
                userSelect: "none",
                // Source PNGs have opaque white backgrounds; multiply makes
                // white transparent so layered components composite cleanly.
                mixBlendMode: "multiply",
              }}
            />
          );
        })}
      </Box>

      {/* Popper's own root carries role="tooltip", so the card inside it
          must not repeat it. */}
      <Popper
        open={!!anchorEl && !!hoveredComponent}
        anchorEl={anchorEl}
        placement="top"
        modifiers={[
          { name: "offset", options: { offset: [0, 12] } },
          { name: "preventOverflow", options: { padding: 8 } },
        ]}
        sx={{ pointerEvents: "none", zIndex: (t) => t.zIndex.tooltip }}
      >
        {hoveredComponent && (
          <Paper elevation={8} sx={CARD_SX}>
            <ComponentStateCard
              label={hoveredComponent.label}
              state={state[hoveredComponent.key] ?? "off"}
              detail={details?.[hoveredComponent.key]}
            />
          </Paper>
        )}
      </Popper>

      {showControls && (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: 1.5 }}
        >
          {components.map((c) => {
            const s = state[c.key] ?? "off";
            return (
              <Tooltip
                key={c.key}
                arrow
                placement="top"
                title={
                  <ComponentStateCard
                    label={c.label}
                    state={s}
                    detail={details?.[c.key]}
                    hint={onToggle ? "Click to cycle this state." : undefined}
                  />
                }
                slotProps={{
                  tooltip: { sx: CARD_SX },
                  arrow: { sx: { color: CARD_BG } },
                }}
              >
                <Chip
                  label={`${c.label}: ${STATE_LABELS[s]}`}
                  onClick={onToggle ? () => onToggle(c.key) : undefined}
                  size="small"
                  sx={{
                    bgcolor: STATE_COLORS[s],
                    color: "#fff",
                    fontWeight: 600,
                    cursor: onToggle ? "pointer" : "help",
                    "&:hover": onToggle
                      ? { bgcolor: STATE_COLORS[s], filter: "brightness(0.9)" }
                      : undefined,
                  }}
                />
              </Tooltip>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

/** Cycle helper exported for parents that wire up `onToggle`. */
export const nextState = (s: ComponentState): ComponentState => {
  const order: ComponentState[] = ["off", "running", "idle", "warning", "error"];
  // "unknown" is data-driven only, so it stays out of the manual toggle cycle.
  return order[(order.indexOf(s) + 1) % order.length];
};

export default ProductionLineDiagram;
